import { Component, Input, OnInit, OnDestroy, CUSTOM_ELEMENTS_SCHEMA, ViewChild, EventEmitter, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast'; 
import { DialogModule } from 'primeng/dialog'; 
import { CardModule } from 'primeng/card'; 
import { DropdownModule } from 'primeng/dropdown'; 
import { AutoCompleteModule } from 'primeng/autocomplete'; 
import { CalendarModule } from 'primeng/calendar'; 
import { DonationCardService } from '../../../services/donation-card.service';
import { AddDonationService } from '../../../services/add-donation.service';
import { DonationCardDonationsGridRow, DonorRelationshipResponse, DonorRelationshipType, DonationComplianceTransactionRowVM } from '../../../models/DonationCardModels';
import { GenericButtonComponent } from '../../../../global/component/generic/generic-button/generic-button.component';
import { InputTextModule } from 'primeng/inputtext';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { MessageService, ConfirmationService } from 'primeng/api';
import { DonorSearchComponent } from '../../donor-search/donor-search.component';
import { Subscription } from 'rxjs';
import { SearchService } from '../../../../global/services/data/search-service/searchservice';
import { DonorFundTotalsGridComponent } from '../../../shared-components/donor-fund-totals-grid/donor-fund-totals-grid.component';
import { restrictToTwoDecimals } from '../../../../global/util/util';

@Component({
  selector: 'app-donation-transaction-history',
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    ButtonModule,
    ToastModule,
    DialogModule,
    CardModule,
    DropdownModule,
    AutoCompleteModule,
    InputTextModule,
    CalendarModule,
    GenericButtonComponent,
    ReactiveFormsModule,
    ToastModule,
    DonorSearchComponent,
    DonorFundTotalsGridComponent
  ],
  templateUrl: './donation-transaction-history.component.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [MessageService, ConfirmationService] 
})
export class DonationTransactionHistoryComponent implements OnInit, OnDestroy {
  @Input() donationId!: number;
  @Input() splitId!: number;
  @Input() campaignId!: number;
  @Input() originalDonationAmount: number = 0;
  @Input() isReadOnlyMode: boolean = false;
  @Output() updateDonationGeneralInfo = new EventEmitter<any>();

  donationTransactions: DonationCardDonationsGridRow[] = [];
  expandedRows: { [key: string]: boolean } = {};
  isDonorEditSidebarVisible: boolean = false; 
  isDonorEditDataLoading: boolean = false; 
  donorToEdit: any; 
  editStep: number = 0;
  complianceActionForm!: FormGroup;
  filteredFundCodes: any[] = [];
  showDonorSearchDialog: boolean = false;
  selectedDonor: any = null;
  selectedComplianceType: string | null = null;
  reattributedDonorId: string | null = null;
  donorName: string | null = null;
  donorCity: string | null = null;
  donorState: string | null = null;
  private subscriptions: Subscription = new Subscription();

  complianceTypeOptions = [
    { label: 'Please select', value: null },
    { label: 'Redisignation', value: '1' },
    { label: 'Reattribution', value: '2' }
  ];

  selectedRattributedDonorId: string | null = null;
  donorRelationships: DonorRelationshipType[] = []; 
  selectedRelationshipType: string | null = null; 

  showDeleteConfirmDialog: boolean = false;
  transactionToDelete: DonationCardDonationsGridRow | null = null;

  isEditDonationDialogVisible: boolean = false;
  editDonationForm!: FormGroup;
  selectedTransaction: DonationCardDonationsGridRow | null = null;
  showRefundAmount: boolean = false; 
  isFundCodeEnabled: boolean = false; 
  editDonationErrorMessage: string | null = null; 

  constructor(
    private donationCardService: DonationCardService,
    private addDonationService: AddDonationService,
    private searchService: SearchService,
    private fb: FormBuilder,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.loadTransactionHistory();
    this.initializeComplianceActionForm();
    this.initializeEditDonationForm();

    this.subscriptions.add(
      this.complianceActionForm.get('complianceType')?.valueChanges.subscribe((value) => {
        this.selectedComplianceType = value;
        this.resetComplianceForm();
        if (value !== '2') {
          this.clearSelectedDonor();
        }
      })
    );

    this.subscriptions.add(
      this.searchService.donorSelected$.subscribe((donor: any) => {
        if (donor) {
          this.fetchDonorRelationships(this.donationId, donor?.DonorID, this.campaignId);
        }
      })
    );

    this.subscriptions.add(
      this.searchService.saveDonor$.subscribe((donor) => {
        if (donor) {
          this.fetchDonorRelationships(this.donationId, donor?.donorId, this.campaignId);
        }
      })
    );
  }

  private getDonorInfomation(donorId: number): void {

    this.addDonationService.getDonorInfo(donorId, this.campaignId).subscribe({
      next: (data) => {
        this.selectedRattributedDonorId = data?.donorId ?? 0;
        this.donorName = [
          data?.prefix,
          data?.firstName,
          data?.lastName,
          data?.suffix
        ].filter(Boolean).join(' ');
        this.donorCity = data?.city;
        this.donorState = data?.state;
        this.selectedDonor = {
          id: this.selectedRattributedDonorId,
          name: this.donorName,
          city: this.donorCity,
          state: this.donorState
        };

        this.showDonorSearchDialog = false;
      }
    });
  }

  private resetComplianceForm(): void {
    this.complianceActionForm.reset({
      complianceType: this.complianceActionForm.get('complianceType')?.value || '', // Retain the current compliance type
      fundCode: '',
      complianceActionAmount: '',
      complianceActionEditDate: '',
      donorRelationship: ''
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  loadTransactionHistory(): void {
    this.donationCardService
      .getDonationTransactionsGrid(this.donationId, this.splitId, this.campaignId)
      .subscribe({
        next: (data: any) => {
          console.debug(data);
          this.donationTransactions = data.map((transaction : any) => ({
            ...transaction,
            isEditable: transaction.type !== 'Parent' // Disable edit for "Parent" type
          }));
        },
        error: (err) => {
          console.error('Unable to retrieve donation transactions. Please try again later.', err);
        },
        complete: () => console.debug('donation-transactions-grid service call complete'),
      });
  }

  calculateRemainingAmount(transaction: DonationCardDonationsGridRow): number {
    const totalAllocated = transaction.amount ?? 0 - (transaction.refundAmount ?? 0);
    return totalAllocated;
  }

  initializeComplianceActionForm(): void {
    this.complianceActionForm = this.fb.group({
      complianceType: ['', Validators.required],
      fundCode: ['', Validators.required],
      complianceActionAmount: ['', [Validators.required, Validators.min(0.01)]],
      complianceActionEditDate: ['', Validators.required],
      donorRelationship: ['']
    });
  }

  initializeEditDonationForm(): void {
    this.editDonationForm = this.fb.group({
      fundCode: [{ value: '', disabled: true }, Validators.required],
      fundCodeId: [null],
      amount: ['', [Validators.required, Validators.min(0)]],
      refundAmount: [''],
      editDate: ['', Validators.required]
    });
  }

  onShowComplianceActionDialog(): void {
    this.showComplianceActionDialog = true;

  }

  onSubmitComplianceAction(): void {
    if (this.isReadOnlyMode) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Read Only',
        detail: 'This tab is in read-only mode. Saving is disabled.'
      });
      return;
    }

    if (this.selectedComplianceType === '2' && !this.complianceActionForm.get('donorRelationship')?.value) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please select a relationship type for reattribution.'
      });
      return;
    }

    if (this.complianceActionForm.valid) {
      const complianceActionData = this.complianceActionForm.value.complianceType === "1"
        ? {
          complianceType: this.complianceActionForm.get('complianceType')?.value,
          donationId: this.donationId,
          fundcodeId: this.complianceActionForm.get('fundCode')?.value?.value || this.complianceActionForm.get('fundCode')?.value,
          amount: this.complianceActionForm.get('complianceActionAmount')?.value,
          editDate: this.complianceActionForm.get('complianceActionEditDate')?.value
        }
        : {
          complianceType: this.complianceActionForm.get('complianceType')?.value,
          donationId: this.donationId,
          fundcodeId: this.complianceActionForm.get('fundCode')?.value?.value || this.complianceActionForm.get('fundCode')?.value,
          donorId: this.selectedRattributedDonorId,
          amount: this.complianceActionForm.get('complianceActionAmount')?.value,
          editDate: this.complianceActionForm.get('complianceActionEditDate')?.value,
          splitId: this.splitId,
          relationshipTypeId: this.complianceActionForm.get('donorRelationship')?.value
        };
      this.donationCardService.submitComplianceAction(complianceActionData).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Compliance action submitted successfully.'
          });
          this.showComplianceActionDialog = false;
          this.complianceActionForm.reset();
          this.loadTransactionHistory();
          this.updateDonationGeneralInfo.emit();
        },
        error: (err: any) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: err?.error?.message ? err?.error?.message : 'Error submitting compliance action. Please try again later.'
          });
          console.error('Error submitting compliance action:', err);
        },
      });
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Form is invalid. Please fill out all required fields.'
      });
    }
  }

  saveComplianceAction(): void {
    if (this.isReadOnlyMode) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Read Only',
        detail: 'This tab is in read-only mode. Saving is disabled.'
      });
      return;
    }
    console.debug('Compliance action saved.');
    this.showComplianceActionDialog = false;
  }

  closeModal(): void {
    this.isDonorEditSidebarVisible = false;
  }

  searchFundCodes(event: any): void {
    const query = event.query;
    this.donationCardService.searchFundCodes(query, this.splitId, this.campaignId).subscribe({
      next: (data: any[]) => {
        this.filteredFundCodes = this.mapToLabelValue(data);
      },
      error: (err: any) => {
        console.error('Error fetching fund codes:', err);
      },
    });
  }

  private mapToLabelValue(items: any[]): { label: string, value: string }[] {
    return items?.map((item: any) => ({ label: item.text, value: item.value })) || [];
  }

  onFundCodeSelected(fundcode: any): void {
    this.complianceActionForm.patchValue({
      fundCode: fundcode.value
    });
  }

  onEditDonationFundCodeSelected(event: any): void {
    this.editDonationForm.patchValue({
      fundCode: event?.value?.label,
      fundCodeId: event?.value?.value
    });
  }

  openDonorSearchDialog(): void {
    this.showDonorSearchDialog = true;
  }

  hideDonorSearchDialog(): void {
    this.showDonorSearchDialog = false;
  }

  onDonorSelected(donor: any): void {
    this.selectedDonor = donor;
    this.selectedRattributedDonorId = donor.id;
    this.showDonorSearchDialog = false;
  }

  private fetchDonorRelationships(donationId: number, selectedDonorId: string, campaignId: number): void {

    this.donationCardService.getDonorRelationships(donationId, selectedDonorId, campaignId).subscribe({
      next: (response: DonorRelationshipResponse) => {
        this.donorRelationships = response.relationshipTypes || [];
        this.complianceActionForm.patchValue({
          donorRelationship: response.relationship?.relationshipTypeId?.toString() || null
        });
        this.getDonorInfomation(Number(selectedDonorId));
      },
      error: (err: any) => {
        console.error('Error fetching donor relationships:', err);
      },
    });
  }

  clearSelectedDonor(): void {
    this.selectedDonor = null;
    this.selectedRattributedDonorId = null;
  }

  confirmDelete(transaction: DonationCardDonationsGridRow): void {
    this.transactionToDelete = transaction;
    this.showDeleteConfirmDialog = true;
  }

  confirmDeleteTransaction(): void {
    if (this.isReadOnlyMode) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Read Only',
        detail: 'This tab is in read-only mode. Deleting is disabled.'
      });
      return;
    }

    if (this.transactionToDelete) {
      this.donationCardService.deleteDonationTransaction(this.transactionToDelete.splitId, this.donationId).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Transaction deleted successfully.'
          });
          this.loadTransactionHistory();
          this.showDeleteConfirmDialog = false;
          this.transactionToDelete = null;

          // Emit the updateDonationGeneralInfo event
          this.updateDonationGeneralInfo.emit();
        },
        error: (err) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to delete the transaction. Please try again later.'
          });
          console.error('Error deleting transaction:', err);
          this.showDeleteConfirmDialog = false;
          this.transactionToDelete = null;
        }
      });
    }
  }

  cancelDeleteTransaction(): void {
    this.showDeleteConfirmDialog = false;
    this.transactionToDelete = null;
  }

  openEditDonationDialog(transaction: DonationCardDonationsGridRow): void {
    this.selectedTransaction = transaction;

    // Reset form controls
    this.editDonationForm.reset();

    // Patch initial values
    this.editDonationForm.patchValue({
      fundCode: transaction.fundCode,
      fundCodeId: transaction.fundcodeId ?? null,
      amount: ((transaction.amount ?? 0) + (transaction.refundAmount ?? 0)),
      refundAmount: transaction.refundAmount ?? null,
      editDate: transaction.editDate ? new Date(transaction.editDate) : null
    });

    // Check transaction type and amount
    const transactionAmount = transaction.amount ?? 0;
    this.showRefundAmount = transactionAmount > 0;
    this.isFundCodeEnabled = transactionAmount > 0;

    if (transaction.type === 'Redesignation' || transaction.type === 'Reattribution') {
      if (transactionAmount < 0) {
        // Disable fundCode, enable amount and date, and validate amount < 0
        this.editDonationForm.get('fundCode')?.disable();
        this.editDonationForm.get('amount')?.setValidators([Validators.required, Validators.max(-0.01)]);
        this.editDonationForm.get('editDate')?.enable();
      } else if (transactionAmount > 0) {
        // Enable fundCode, amount, and date, and validate amount > 0
        this.editDonationForm.get('fundCode')?.enable();
        this.editDonationForm.get('amount')?.setValidators([Validators.required, Validators.min(0.01)]);
        this.editDonationForm.get('editDate')?.enable();
      }
      this.editDonationForm.get('amount')?.updateValueAndValidity();
    } else {
      // Default behavior for other transaction types
      this.editDonationForm.get('fundCode')?.enable();
      this.editDonationForm.get('amount')?.setValidators([Validators.required, Validators.min(0)]);
      this.editDonationForm.get('editDate')?.enable();
      this.editDonationForm.get('amount')?.updateValueAndValidity();
    }

    this.isEditDonationDialogVisible = true;
  }

  saveEditedDonation(): void {
    if (this.isReadOnlyMode) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Read Only',
        detail: 'This tab is in read-only mode. Saving is disabled.'
      });
      return;
    }

    if (this.editDonationForm.valid && this.selectedTransaction) {
      const updatedTransaction: DonationComplianceTransactionRowVM = {
        donationId: this.donationId,
        splitId: this.selectedTransaction.splitId,
        fundCodeId: this.editDonationForm.get('fundCodeId')?.value ?? null, // Retrieve fundCodeId from the form
        amount: this.editDonationForm.get('amount')?.value,
        refundAmount: this.editDonationForm.get('refundAmount')?.value ?? null,
        editDate: this.editDonationForm.get('editDate')?.value,
        complianceActionTypId: this.selectedTransaction.correctiveActionTypeId ?? null,
      };

      this.donationCardService.updateDonationTransaction(updatedTransaction).subscribe({
        next: () => {
          this.isEditDonationDialogVisible = false;
          this.editDonationErrorMessage = null;
          this.loadTransactionHistory();
        },
        error: (err) => {
          this.editDonationErrorMessage = err?.error?.message ? err.error.message : 'Failed to update donation transaction. Please try again.';
          console.error('Error updating donation transaction:', err);
        }
      });
    }
  }

  closeEditDonationDialog(): void {
    this.isEditDonationDialogVisible = false;
    this.editDonationForm.reset();
    this.selectedTransaction = null;
    this.editDonationErrorMessage = null;
  }

  set showComplianceActionDialog(value: boolean) {
    if (value) {
      this.complianceActionForm.reset();
    }
    this._showComplianceActionDialog = value;
  }

  get showComplianceActionDialog(): boolean {
    return this._showComplianceActionDialog;
  }

  onAmountInput(event: Event, controlName: string, formName: string = ''): void {
    const input = event.target as HTMLInputElement;
    const formattedValue = restrictToTwoDecimals(input.value);
    input.value = formattedValue;
    this.complianceActionForm.get(controlName)?.setValue(formattedValue, { emitEvent: false });
  }

  private _showComplianceActionDialog: boolean = false;
}

